import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import { useTaxRegimes } from "@/modules/settings/SettingsPage";
import { DocumentEditor, emptyLine, type EditorDoc } from "@/modules/documents/DocumentEditor";
import { useInvoice, useSaveInvoiceDraft, invoiceToDoc, INVOICE_TYPE_LABEL, type InvoiceHeader } from "./api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/** Modification d'un brouillon de facture ou d'avoir (/factures/:id/modifier). */
export default function InvoiceEditorPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const existing = useInvoice(id);
  const { data: regimes } = useTaxRegimes(true);
  const save = useSaveInvoiceDraft();
  const [header, setHeader] = useState<InvoiceHeader>({ title: "", issue_date: new Date().toISOString().slice(0, 10), payment_days: 30, tax_label: null, tax_rate_pct: 0, tax_mention: null, notes_client: "", notes_internal: "" });
  const [doc, setDoc] = useState<EditorDoc>({ currency: "EUR", lines: [emptyLine()], fees: [], global_discount_type: null, global_discount_value: null, tax_rate_pct: 0, tax_label: null, deposit_pct: 0 });
  const [loadedFrom, setLoadedFrom] = useState<string | null>(null);

  // Caler l'éditeur sur la facture chargée, une seule fois : ajustement d'état
  // pendant le rendu (motif React) plutôt qu'un effet, pour éviter un premier
  // affichage avec un formulaire vide.
  const loaded = existing.data;
  if (loaded && loadedFrom !== loaded.id && loaded.status === "draft") {
    setHeader({ title: loaded.title ?? "", issue_date: loaded.issue_date, payment_days: loaded.payment_days, tax_label: loaded.tax_label, tax_rate_pct: Number(loaded.tax_rate_pct), tax_mention: loaded.tax_mention, notes_client: loaded.notes_client ?? "", notes_internal: loaded.notes_internal ?? "" });
    const d = invoiceToDoc(loaded); setDoc(d.lines.length ? d : { ...d, lines: [emptyLine()] });
    setLoadedFrom(loaded.id);
  }

  // Une facture émise ne s'édite plus : retour à sa fiche (action → effet).
  useEffect(() => {
    if (existing.data && existing.data.status !== "draft") nav(`/factures/${existing.data.id}`, { replace: true });
  }, [existing.data, nav]);

  const inv = existing.data;
  const isCredit = inv?.type === "credit_note";
  const canSave = useMemo(() => doc.lines.length > 0 && doc.lines.every((l) => l.label.trim() && l.quantity > 0 && l.unit_price >= 0), [doc]);
  const regimeId = regimes?.find((r) => r.label === header.tax_label && Number(r.rate_pct) === header.tax_rate_pct)?.id ?? "";

  function setRegime(rid: string) {
    const r = regimes?.find((x) => x.id === rid); if (!r) return;
    setHeader((h) => ({ ...h, tax_label: r.label, tax_rate_pct: Number(r.rate_pct), tax_mention: r.mention }));
    setDoc((d) => ({ ...d, tax_rate_pct: Number(r.rate_pct), tax_label: r.label }));
  }
  async function submit() {
    if (!inv) return;
    if (!canSave) { toast.error("Complète chaque ligne (libellé, quantité, prix)."); return; }
    await save.mutateAsync({ id: inv.id, header: { ...header, title: header.title || null, notes_client: header.notes_client || null, notes_internal: header.notes_internal || null }, doc });
    toast.success("Brouillon enregistré"); nav(`/factures/${inv.id}`);
  }

  if (existing.isLoading) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  if (!inv) return <p className="text-sm text-destructive">Facture introuvable.</p>;

  return (
    <div>
      <Link to={`/factures/${inv.id}`} className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Retour</Link>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">{INVOICE_TYPE_LABEL[inv.type]} — brouillon</h1>
          <p className="mt-1 text-sm text-muted-foreground">{inv.clients?.legal_name}{inv.orders ? ` · commande ${inv.orders.number}` : ""}. Le numéro sera attribué à l'émission ; ensuite plus rien ne bouge.</p>
        </div>
        <Button onClick={() => void submit()} disabled={save.isPending || !canSave}><Save className="mr-2 h-4 w-4" />Enregistrer le brouillon</Button>
      </div>

      <section className="glass mb-6 rounded-2xl p-5">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-1.5"><Label htmlFor="i-title">Objet</Label><Input id="i-title" value={header.title ?? ""} onChange={(e) => setHeader({ ...header, title: e.target.value })} /></div>
          <div className="space-y-1.5">
            <Label htmlFor="i-currency">Devise</Label>
            <Select value={doc.currency} onValueChange={(v) => setDoc({ ...doc, currency: v as "EUR" | "USD" })} disabled={isCredit || Boolean(inv.order_id)}>
              <SelectTrigger id="i-currency"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="EUR">EUR</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label htmlFor="i-issue">Date</Label><Input id="i-issue" type="date" value={header.issue_date} onChange={(e) => setHeader({ ...header, issue_date: e.target.value })} /></div>
          {!isCredit && <div className="space-y-1.5"><Label htmlFor="i-days">Délai de paiement (jours)</Label><Input id="i-days" type="number" min="0" max="180" value={header.payment_days} onChange={(e) => setHeader({ ...header, payment_days: Number(e.target.value) || 0 })} /></div>}
          <div className="space-y-1.5">
            <Label htmlFor="i-tax">Régime fiscal</Label>
            <Select value={regimeId} onValueChange={setRegime} disabled={isCredit}>
              <SelectTrigger id="i-tax"><SelectValue placeholder={header.tax_label ?? "Choisir"} /></SelectTrigger>
              <SelectContent>{regimes?.map((r) => <SelectItem key={r.id} value={r.id}>{r.label} — {Number(r.rate_pct)} %</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <DocumentEditor doc={doc} onChange={setDoc} clientId={inv.client_id} hideDeposit />

      <section className="glass mt-6 grid gap-4 rounded-2xl p-5 md:grid-cols-2">
        <div className="space-y-1.5"><Label htmlFor="i-notes">{isCredit ? "Motif (imprimé sur l'avoir)" : "Notes pour le client (imprimées sur la facture)"}</Label><Textarea id="i-notes" rows={3} value={header.notes_client ?? ""} onChange={(e) => setHeader({ ...header, notes_client: e.target.value })} /></div>
        <div className="space-y-1.5"><Label htmlFor="i-internal">Notes internes</Label><Textarea id="i-internal" rows={3} value={header.notes_internal ?? ""} onChange={(e) => setHeader({ ...header, notes_internal: e.target.value })} /></div>
      </section>
      <div className="mt-6 flex justify-end"><Button onClick={() => void submit()} disabled={save.isPending || !canSave}><Save className="mr-2 h-4 w-4" />Enregistrer le brouillon</Button></div>
    </div>
  );
}
